import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import styled from 'styled-components';
import { Modal } from 'src/components/modals/comps';
import Input, { Textarea } from 'src/components/form/Input';
import Select from 'src/components/form/Select';
import Button from 'src/components/form/Button';
import { InputLabel, Poppins, Spacer } from 'src/common';
import { showSuccessToast } from 'src/components/Toast';
import { useAuth } from 'src/state/auth';
import { ScenarioGroupSummary } from 'src/graphql';
import {
  UPDATE_SCENARIO_GROUP_METADATA,
  UpdateScenarioGroupMetadataMutation,
  UpdateScenarioGroupMetadataMutationVariables,
} from 'src/graphql/contentImport';

const StyledModal = styled(Modal)`
  width: 760px;
  padding: 0 !important;
`;

const Content = styled.div`
  padding: 34px 30px 30px;
`;

const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.white};
  padding: 20px;
`;

const FormInput = styled(Input)`
  &[type='text'] {
    padding: 0 18px;
  }
`;

const FormTextarea = styled(Textarea)`
  min-height: 118px;
  padding: 14px 18px;
`;

const FormSelect = styled(Select)`
  width: 100%;
  min-width: 0;
`;

const ButtonsRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

interface AuthorOption {
  value: string;
  label: string;
  profilePhotoPath: string | null;
}

interface GroupOption {
  value: string;
  label: string;
}

interface EditScenarioPackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEdited: () => Promise<void> | void;
  groups: ScenarioGroupSummary[];
}

export const EditScenarioPackModal: React.FC<EditScenarioPackModalProps> = ({ isOpen, onClose, onEdited, groups }) => {
  const { usersOptios } = useAuth();

  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [packName, setPackName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [author, setAuthor] = useState<AuthorOption | null>(null);
  const [error, setError] = useState('');

  const [runUpdateMetadata, { loading: isSaving }] = useMutation<
    UpdateScenarioGroupMetadataMutation,
    UpdateScenarioGroupMetadataMutationVariables
  >(UPDATE_SCENARIO_GROUP_METADATA);

  const authorOptions = useMemo<AuthorOption[]>(() => {
    const seen = new Set<string>();
    const options: AuthorOption[] = [];

    for (const owner of usersOptios || []) {
      const label = owner.text || owner.email || '';
      if (!label) continue;

      const key = `${owner.id || ''}::${label.toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);

      options.push({
        value: owner.id || owner.email || owner.text || label,
        label,
        profilePhotoPath: owner.profilePhotoPath || null,
      });
    }

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [usersOptios]);

  const groupOptions = useMemo<GroupOption[]>(() => {
    return (groups || [])
      .map((group) => ({
        value: group.id,
        label: group.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [groups]);

  const selectedGroup = useMemo(
    () => groups.find((group) => group.id === selectedGroupId) || null,
    [groups, selectedGroupId],
  );

  useEffect(() => {
    if (!isOpen) return;

    const firstGroup = groups[0];
    if (!firstGroup) {
      setSelectedGroupId('');
      setPackName('');
      setDescription('');
      setCategory('');
      setAuthor(null);
      return;
    }

    setSelectedGroupId(firstGroup.id);
    setPackName(firstGroup.name || '');
    setDescription(firstGroup.description || '');
    setCategory(firstGroup.category || '');

    const existingAuthor = (firstGroup.author || '').trim().toLowerCase();
    const matchedAuthor = authorOptions.find((option) => option.label.trim().toLowerCase() === existingAuthor) || null;
    if (matchedAuthor) {
      setAuthor(matchedAuthor);
    } else if ((firstGroup.author || '').trim()) {
      setAuthor({
        value: firstGroup.author!.trim(),
        label: firstGroup.author!.trim(),
        profilePhotoPath: (firstGroup.authorLogoPath || null) as string | null,
      });
    } else {
      setAuthor(null);
    }
    setError('');
  }, [isOpen, groups, authorOptions]);

  useEffect(() => {
    if (!selectedGroup) return;

    setPackName(selectedGroup.name || '');
    setDescription(selectedGroup.description || '');
    setCategory(selectedGroup.category || '');

    const existingAuthor = (selectedGroup.author || '').trim().toLowerCase();
    const matchedAuthor = authorOptions.find((option) => option.label.trim().toLowerCase() === existingAuthor) || null;
    if (matchedAuthor) {
      setAuthor(matchedAuthor);
    } else if ((selectedGroup.author || '').trim()) {
      setAuthor({
        value: selectedGroup.author!.trim(),
        label: selectedGroup.author!.trim(),
        profilePhotoPath: (selectedGroup.authorLogoPath || null) as string | null,
      });
    } else {
      setAuthor(null);
    }
  }, [selectedGroup, authorOptions]);

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const groupSelectValue = groupOptions.find((option) => option.value === selectedGroupId) || null;
  const canSave = !!selectedGroup && !!packName.trim() && !isSaving;

  const onSave = async () => {
    if (!selectedGroup || !packName.trim()) return;

    setError('');

    try {
      await runUpdateMetadata({
        variables: {
          scenarioGroupId: selectedGroup.id,
          group: {
            id: selectedGroup.id,
            name: packName.trim(),
            description: description.trim() || null,
            author: author?.label || null,
            authorLogoPath: author?.profilePhotoPath || null,
            category: category.trim() || null,
            isSystem: selectedGroup.isSystem ?? true,
          },
        },
      });

      await onEdited();
      showSuccessToast({ title: 'Scenario pack updated' });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Scenario pack update failed.');
    }
  };

  return (
    <StyledModal isOpen={isOpen} onRequestClose={handleClose} persistent={isSaving}>
      <Content>
        <Poppins className="m-title" px={32}>
          Edit scenario pack
        </Poppins>

        <Spacer $px={18} />
        <Panel>
          <InputLabel>SCENARIO PACK</InputLabel>
          <Spacer $px={8} />
          <FormSelect
            options={groupOptions}
            value={groupSelectValue}
            onChange={(nextValue: unknown) => setSelectedGroupId(((nextValue as GroupOption | null)?.value || '').trim())}
            placeholder="Select scenario pack"
            isDisabled={isSaving || !groupOptions.length}
          />

          <Spacer $px={16} />
          <InputLabel>NAME</InputLabel>
          <Spacer $px={8} />
          <FormInput
            value={packName}
            onChange={(e) => setPackName(e.target.value)}
            placeholder="Enter scenario pack name"
            maxLength={255}
            disabled={!selectedGroup || isSaving}
          />

          <Spacer $px={16} />
          <InputLabel>DESCRIPTION</InputLabel>
          <Spacer $px={8} />
          <FormTextarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe this scenario pack"
            maxLength={2048}
            disabled={!selectedGroup || isSaving}
          />

          <Spacer $px={16} />
          <InputLabel>AUTHOR</InputLabel>
          <Spacer $px={8} />
          <FormSelect
            placeholder="Select author"
            options={authorOptions}
            value={author}
            onChange={(nextValue: unknown) => setAuthor((nextValue as AuthorOption | null) || null)}
            isClearable
            isDisabled={!selectedGroup || isSaving}
          />

          <Spacer $px={16} />
          <InputLabel>CATEGORY</InputLabel>
          <Spacer $px={8} />
          <FormInput
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Enter category"
            maxLength={255}
            disabled={!selectedGroup || isSaving}
          />
        </Panel>

        {!!error && (
          <>
            <Spacer $px={14} />
            <Poppins px={14} color="error">
              {error}
            </Poppins>
          </>
        )}

        {!groupOptions.length && (
          <>
            <Spacer $px={14} />
            <Poppins px={14} color="error">
              No scenario packs available to edit in this workspace.
            </Poppins>
          </>
        )}

        <Spacer $px={20} />
        <ButtonsRow>
          <Button primary style={{ minWidth: 280 }} onClick={onSave} disabled={!canSave} loading={isSaving}>
            Save scenario pack
          </Button>
        </ButtonsRow>
      </Content>
    </StyledModal>
  );
};
